import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Button from '@components/button/Button'
import SectionTitle from '@components/sectionTitle/SectionTitle'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as AuthStore from '@store/auth/auth'
import * as ControleStore from '@store/controle/controle'

import { downloadDocument } from '@screens/documents/documentsUtils'
import { findTenant, findManifestation, getEDLTypeTenant } from '@screens/edl/utils'
import { isManifestationIN, isManifestationOUT } from '@screens/edl/list/useEDL'

import { EtatDesLieuxTypeEnum, AuthScope } from '@api/enum'

import { createUUID } from '@utils/uuid'
import { isManifestationValidForCQ } from '@screens/controle/form/utils'

interface Props {
  documents: Documents
  id: string
  isManifestation: boolean
  edlList: EtatDesLieuxResponse[]
  isAlone?: boolean
}

const DocumentsSection = ({ documents, id, edlList, isAlone }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const newId = createUUID()

  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const manifestationDetails = React.useMemo(() => findManifestation(manifestations, id), [manifestations, id])
  const tenantDetails = React.useMemo(() => findTenant(tenants, id), [tenants, id])

  const canCreateEntryEDL =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.EdlEntreeCreation)
  const canCreateExitEDL =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.EdlSortieCreation)

  const canManifEntryEdl =
    !!manifestationDetails && manifestationDetails.actif && canCreateEntryEDL && isManifestationIN(manifestationDetails)

  const canManifExitEdl =
    !!manifestationDetails && manifestationDetails.actif && canCreateExitEDL && isManifestationOUT(manifestationDetails)

  const canTenantEntryEdl = React.useMemo(
    () => !!tenantDetails && getEDLTypeTenant(tenantDetails) === EtatDesLieuxTypeEnum.ENTREE,
    [tenantDetails]
  )
  const canTenantExitEdl = React.useMemo(
    () => !!tenantDetails && getEDLTypeTenant(tenantDetails) === EtatDesLieuxTypeEnum.SORTIE,
    [tenantDetails]
  )

  const canCreateChecklist =
    !!manifestationDetails && manifestationDetails.actif && scopes.includes(AuthScope.CheckListSiteCreation)

  const canCreateCQ = !!manifestationDetails && isManifestationValidForCQ(manifestationDetails, new Date())

  const entryEdlNumber = edlList.filter((e) => e.type === EtatDesLieuxTypeEnum.ENTREE).length
  const exitEdlNumber = edlList.filter((e) => e.type === EtatDesLieuxTypeEnum.SORTIE).length

  const renderDownloadButton = (document: RelatedDocument, name: string) => (
    <Button status="active" label={name} onClick={() => downloadDocument(document)} leftIcon="download" />
  )

  return (
    <DocumentsContainer isAlone={isAlone}>
      <TitleContainer>
        <SectionTitle title={i18n.t('components.documentsSection.title')} />
      </TitleContainer>
      <DocumentsRow>
        <ButtonCard>
          <ButtonTitle>{i18n.t('components.documentsSection.edl')}</ButtonTitle>
          <View>
            {(canManifEntryEdl || canTenantEntryEdl) && canCreateEntryEDL && (
              <ButtonContainer>
                <Button
                  status="active"
                  color="light"
                  label={i18n.t('components.documentsSection.startEntryEDL')}
                  onClick={() =>
                    navigation.push(`/edl/spaces/${newId}?type=ENTREE&manifestationId=${id}&origin=manifestation`)
                  }
                />
              </ButtonContainer>
            )}
            {(canManifExitEdl || canTenantExitEdl) && canCreateExitEDL && (
              <ButtonContainer>
                <Button
                  status="active"
                  color="light"
                  label={i18n.t('components.documentsSection.startExitEDL')}
                  onClick={() =>
                    navigation.push(`/edl/spaces/${newId}?type=SORTIE&manifestationId=${id}&origin=manifestation`)
                  }
                />
              </ButtonContainer>
            )}
            {(entryEdlNumber > 0 || exitEdlNumber > 0) && (
              <ButtonContainer>
                <Button
                  status="active"
                  color="light"
                  label={i18n.t('components.documentsSection.edlList')}
                  onClick={() => navigation.push(`/edl?manifestationId=${id}&list=finished`)}
                />
              </ButtonContainer>
            )}
          </View>
        </ButtonCard>

        <ButtonCard isCenter>
          <ButtonTitle>{i18n.t('components.documentsSection.checklist')}</ButtonTitle>
          <ButtonContainer>
            {!!documents.checklist
              ? renderDownloadButton(documents.checklist, i18n.t('screens.documents.checklist'))
              : canCreateChecklist && (
                  <Button
                    status="active"
                    color="light"
                    label={i18n.t('components.documentsSection.startChecklist')}
                    onClick={() => navigation.push('/checklist')}
                  />
                )}
          </ButtonContainer>
        </ButtonCard>

        <ButtonCard>
          <ButtonTitle>{i18n.t('components.documentsSection.quality')}</ButtonTitle>
          <View>
            {documents.quality.length > 0 && (
              <ButtonContainer>
                <Button
                  status="active"
                  color="light"
                  label={i18n.t('screens.documents.quality', { count: documents.quality.length })}
                  onClick={() => navigation.push(`/controles/history/list?manifestationId=${id}`)}
                />
              </ButtonContainer>
            )}
            {canCreateCQ && (
              <ButtonContainer>
                <Button
                  status="active"
                  color="light"
                  label={i18n.t('components.documentsSection.startControl')}
                  onClick={() => {
                    ControleStore.actions.resetForm()
                    navigation.push(`/controles/form/presta?manifestationId=${id}`)
                  }}
                />
              </ButtonContainer>
            )}
          </View>
        </ButtonCard>
      </DocumentsRow>
    </DocumentsContainer>
  )
}

export default DocumentsSection

const DocumentsContainer = styled(View)<{ isAlone?: boolean }>`
  ${(props) => props.theme.breakpoint === 'big' && `flex: 2; ${!props.isAlone && 'margin-left: 16px;'}`}
`
const TitleContainer = styled(View)`
  height: 48px;
  justify-content: center;
`
const DocumentsRow = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint !== 'medium' ? 'row' : 'column')};
  margin: 16px 0;
`
const ButtonTitle = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
`
const ButtonCard = styled(View)<{ isCenter?: boolean }>`
  padding: ${(props) => (props.theme.breakpoint !== 'medium' ? '24px' : '12px')};
  background-color: ${(props) => props.theme.colors.incident};
  flex-direction: ${(props) => (props.theme.breakpoint !== 'medium' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint === 'medium' && 'align-items: center;'};

  ${(props) => (props.theme.breakpoint === 'medium' ? 'margin-bottom: 8px;' : 'flex: 1;')};
  ${(props) => props.theme.breakpoint === 'medium' && `justify-content: space-between;`}
  ${(props) => props.isCenter && props.theme.breakpoint !== 'medium' && `margin: 0px 16px;`}
`
const ButtonContainer = styled(View)`
  margin-top: 8px;
`
